import { useEffect } from "react";
import { addPartnerTeams, updateUserGroup } from "../../../services/PartnerService";

export const initialTeamRevealState = {
  choice: "rename",
  isRenamingDefault: false,
  teamName: "",
};

// Shared reveal step used by AddUserModal and ActivateWalletModal on the
// 1-to-2 wallet-holder crossing. It offers two mutually exclusive paths:
// keep the existing default team (with an optional inline rename) or
// create a new team for the invitee. Parents own the state so form
// validity and reset semantics stay in one place.
export function isTeamRevealValid(state) {
    if (state.choice === "rename") {
        if (!state.isRenamingDefault) return true;
        return state.teamName.trim().length > 0;
    }
    if (state.choice === "new") {
        return state.teamName.trim().length > 0;
    }
    return false;
}

export async function resolveTeamIdFromReveal({ state, partnerId, defaultTeam, queryClient }) {
    if (state.choice === "rename") {
        if (state.isRenamingDefault) {
            const trimmed = state.teamName.trim();
            if (defaultTeam && trimmed && trimmed !== defaultTeam.name) {
                await updateUserGroup(partnerId, defaultTeam.id, { name: trimmed });
                queryClient.invalidateQueries({ queryKey: ["allPartnerTeams", partnerId] });
            }
        }
        return defaultTeam?.id ?? null;
    }

    const trimmedNew = state.teamName.trim();
    const newTeamRes = await addPartnerTeams(partnerId, { name: trimmedNew });
    queryClient.invalidateQueries({ queryKey: ["allPartnerTeams", partnerId] });
    return newTeamRes?.data?.data?.id;
}

const TeamRevealStep = ({
  firstName,
  defaultTeam,
  roleGrantsLimitEditing,
  state,
  onStateChange,
}) => {
    const nameOrThey = firstName?.trim() || "they";

    useEffect(() => {
        if (state.choice !== "rename") return;
        if (defaultTeam) return;
        onStateChange({
            choice: "new",
            isRenamingDefault: false,
            teamName: "",
        });
    }, [state.choice, defaultTeam, onStateChange]);

    const setChoice = (nextChoice) => {
        onStateChange({
            choice: nextChoice,
            isRenamingDefault: false,
            teamName: "",
        });
    };

    const startRename = () => {
        onStateChange({
            ...state,
            isRenamingDefault: true,
            teamName: defaultTeam?.name ?? "",
        });
    };

    const cancelRename = () => {
        onStateChange({
            ...state,
            isRenamingDefault: false,
            teamName: "",
        });
    };

    const setTeamName = (nextName) => {
        onStateChange({ ...state, teamName: nextName });
    };

    return (
        <div className="mb-3 p-4 border rounded" data-tour="users-add-team-reveal">
            <div className="fw-bold text-body mb-4" style={{ fontSize: "1.05rem" }}>
                Which team will {nameOrThey} join?
            </div>

            <div className="form-check mb-2">
                <input
                    type="radio"
                    className="form-check-input"
                    id="revealChoiceRename"
                    name="revealChoice"
                    checked={state.choice === "rename"}
                    onChange={() => setChoice("rename")}
                    disabled={!defaultTeam}
                />
                <label className="form-check-label" htmlFor="revealChoiceRename">
                    Your team{!defaultTeam && (
                        <span className="text-muted small ms-2">(a default team is not set on this account, pick a new team below)</span>
                    )}
                </label>
            </div>
            {state.choice === "rename" && (
                <div className="ps-4 mb-4">
                    {state.isRenamingDefault ? (
                        <>
                            <label className="form-label small text-muted" htmlFor="revealRenameInput">Team name</label>
                            <input
                                type="text"
                                id="revealRenameInput"
                                className="form-control"
                                value={state.teamName}
                                onChange={(e) => setTeamName(e.target.value)}
                                autoFocus
                            />
                            <button
                                type="button"
                                className="btn btn-sm btn-link p-0 mt-2"
                                onClick={cancelRename}
                            >
                                Cancel rename
                            </button>
                        </>
                    ) : (
                        <div className="d-flex align-items-center gap-3">
                            <span className="text-muted small">Team</span>
                            <span className="fw-bold text-body">{defaultTeam?.name ?? "loading..."}</span>
                            {defaultTeam && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link p-0"
                                    onClick={startRename}
                                >
                                    Rename
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="form-check mb-2">
                <input
                    type="radio"
                    className="form-check-input"
                    id="revealChoiceNew"
                    name="revealChoice"
                    checked={state.choice === "new"}
                    onChange={() => setChoice("new")}
                />
                <label className="form-check-label" htmlFor="revealChoiceNew">
                    A new team
                </label>
            </div>
            {state.choice === "new" && (
                <div className="ps-4 mb-4">
                    <label className="form-label small text-muted" htmlFor="revealNewInput">New team name</label>
                    <input
                        type="text"
                        id="revealNewInput"
                        className="form-control"
                        value={state.teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        placeholder="e.g. Operations"
                        autoFocus
                    />
                </div>
            )}

            {roleGrantsLimitEditing && (
                <div className="mt-4 pt-3 border-top">
                    <div className="small text-body mb-2">
                        <strong>
                            Once {firstName?.trim() ? `${firstName.trim()} is` : "they are"} added, you will no longer be able to change your own spending limit.
                        </strong>
                        {" "}Limit changes now go through peer review for accountability.
                    </div>
                    <div className="small text-body">
                        To request a change, submit an extension request from the Approvals page. <strong>Any other super admin can approve it.</strong>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamRevealStep;
